import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import Seo from "../../components/Seo";

const empty = { name: "", description: "", image: "", connectivity: "", schools: "", hospitals: "", market: "", investment: "", seo_title: "", seo_description: "" };
const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm";
const num = (v) => (v === "" ? null : Number(v));

export default function AdminLocations() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-locations"], queryFn: async () => (await api.get("/locations")).data });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const openNew = () => { setF(empty); setEditId(null); setOpen(true); };
  const openEdit = (l) => { setF({ ...empty, ...l, connectivity: l.connectivity ?? "", schools: l.schools ?? "", hospitals: l.hospitals ?? "", market: l.market ?? "", investment: l.investment ?? "" }); setEditId(l.id); setOpen(true); };

  const save = async () => {
    if (!f.name) return toast.error("Name required");
    const payload = { ...f, connectivity: num(f.connectivity), schools: num(f.schools), hospitals: num(f.hospitals), market: num(f.market), investment: num(f.investment) };
    try {
      if (editId) await api.put(`/admin/locations/${editId}`, payload);
      else await api.post("/admin/locations", payload);
      toast.success("Saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["admin-locations"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (l) => { if (!window.confirm(`Delete ${l.name}?`)) return; await api.delete(`/admin/locations/${l.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-locations"] }); };

  return (
    <div>
      <Seo title="Locations | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Locations / Sectors</h1><p className="text-slate-500">Manage Faridabad sectors and colonies.</p></div>
        <Button onClick={openNew} className="bg-indigo-900 hover:bg-indigo-800" data-testid="add-location-btn"><Plus className="w-4 h-4 mr-2" /> Add Location</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {(data?.items || []).map((l) => (
          <div key={l.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`admin-location-${l.slug}`}>
            <div className="flex items-start justify-between">
              <div><h3 className="font-heading font-bold text-slate-900">{l.name}</h3><span className="text-xs text-slate-400">{l.property_count} properties</span></div>
              <div className="flex gap-1"><button onClick={() => openEdit(l)} className="p-1.5 hover:bg-slate-100 rounded-lg"><Pencil className="w-4 h-4 text-slate-500" /></button><button onClick={() => del(l)} className="p-1.5 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4 text-rose-500" /></button></div>
            </div>
            <p className="text-sm text-slate-500 mt-2 line-clamp-2">{l.description}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name (e.g. Sector 88)" className={inp} data-testid="loc-name" />
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className={inp} />
            <input value={f.image} onChange={(e) => set("image", e.target.value)} placeholder="Image URL" className={inp} />
            <div className="grid grid-cols-5 gap-2">
              {[["connectivity", "Conn."], ["schools", "Schools"], ["hospitals", "Hosp."], ["market", "Market"], ["investment", "Invest"]].map(([k, l]) => (
                <div key={k}><label className="text-xs text-slate-500">{l}</label><input type="number" step="0.1" value={f[k]} onChange={(e) => set(k, e.target.value)} className={inp} /></div>
              ))}
            </div>
            <input value={f.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="SEO Title" className={inp} />
            <textarea value={f.seo_description} onChange={(e) => set("seo_description", e.target.value)} placeholder="SEO Description" rows={2} className={inp} />
          </div>
          <DialogFooter><Button onClick={save} className="bg-indigo-900 hover:bg-indigo-800" data-testid="loc-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
